import React, { useEffect, useState } from "react";
import { MdFileDownload } from "react-icons/md";

import { apiGetArtworkTemplates } from "../api";

/**
 * The download panel an image upload screen shows for its own slot.
 * ────────────────────────────────────────────────────────────────────────────
 * Drop this next to any "File requirements" block:
 *
 *   <ArtworkTemplateLink slotKey="restaurant.shopBanner" />
 *
 * It renders nothing at all when no template has been uploaded for that slot,
 * so adding it to a screen is safe before anyone has uploaded anything — a
 * download button that leads nowhere is worse than no button.
 *
 * The panel variant shows a thumbnail of page 1, because "download this and
 * design against it" is a much easier instruction to follow when you can see
 * what you are about to get.
 *
 * ONE REQUEST FOR THE WHOLE APP. Several of these can be on screen at once (a
 * profile image and a banner on the same form), and more still across a
 * session. The fetch is a module-level promise shared by every instance and
 * held for the life of the page, so mounting ten of these costs one call.
 * `refreshArtworkTemplates()` clears it — the admin settings screen calls that
 * after an upload so the rest of the app sees the change without a reload.
 */
let templatesPromise = null;

const loadTemplates = () => {
  if (!templatesPromise) {
    templatesPromise = apiGetArtworkTemplates()
      .then((res) => (Array.isArray(res?.data?.data) ? res.data.data : []))
      .catch(() => {
        // Non-fatal by design: this is guidance, not a dependency. Clear the
        // cached rejection so a later mount can retry.
        templatesPromise = null;
        return [];
      });
  }
  return templatesPromise;
};

export const refreshArtworkTemplates = () => {
  templatesPromise = null;
};

const ArtworkTemplateLink = ({
  slotKey,
  // "panel" matches the boxed callouts on the product forms; "inline" is a
  // compact row for sitting inside an existing File requirements block.
  variant = "panel",
  className = "",
  style = {},
}) => {
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadTemplates().then((rows) => {
      if (cancelled) return;
      setTemplate(rows.find((row) => row.slotKey === slotKey) || null);
    });
    return () => {
      cancelled = true;
    };
  }, [slotKey]);

  if (!template?.url) return null;

  const href = template.downloadUrl || template.url;

  if (variant === "inline") {
    return (
      <div
        className={`d-flex align-items-center ${className}`}
        style={{ gap: 10, marginTop: 8, ...style }}
      >
        {template.previewImageUrl ? (
          <img
            src={template.previewImageUrl}
            alt=""
            style={{
              width: 40,
              height: 40,
              objectFit: "contain",
              background: "white",
              border: "1px solid #e2e8f0",
              borderRadius: 6,
              flexShrink: 0,
            }}
          />
        ) : null}
        <div style={{ minWidth: 0 }}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: "0.8rem",
              fontWeight: 600,
              color: "#0a578bff",
              textDecoration: "none",
            }}
          >
            <MdFileDownload size={18} />
            Download the artwork template
          </a>
          {template.notes ? (
            <div style={{ fontSize: "0.72rem", color: "#64748b" }}>
              {template.notes}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`d-flex align-items-center ${className}`}
      style={{
        gap: 14,
        border: "1px solid #dbeafe",
        background: "#f5f9ff",
        borderRadius: 12,
        padding: 16,
        marginBottom: 24,
        ...style,
      }}
    >
      {template.previewImageUrl ? (
        <img
          src={template.previewImageUrl}
          alt="Artwork template preview"
          style={{
            width: 56,
            height: 56,
            objectFit: "contain",
            background: "white",
            border: "1px solid #dbeafe",
            borderRadius: 8,
            flexShrink: 0,
          }}
        />
      ) : (
        <MdFileDownload size={30} style={{ color: "#0a578bff", flexShrink: 0 }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: "#0a578bff", fontSize: "0.95rem" }}>
          Artwork template
        </div>
        <div style={{ fontSize: "0.78rem", color: "#475569" }}>
          {template.notes ||
            "Start from the template instead of a blank canvas — download it, open it in Photoshop, Illustrator or whichever editor you prefer, then upload the finished image here."}
        </div>
      </div>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-primary"
        style={{ flexShrink: 0, textDecoration: "none", color: "white" }}
      >
        Download template
      </a>
    </div>
  );
};

export default ArtworkTemplateLink;
